import {Button, Card, message, Popconfirm} from 'antd';
import {CardTitle} from './CardDataDisplay';
import {useTranslation} from '../../../i18n/i18n';
import './FilesCard.scss';
import AddFileModal from './AddFileModal';
import {File} from '../../../gen/proto/api/gateway/v1/file_pb';
import {useEffect, useState} from 'react';
import {filesApi} from '../../../api/files.api';
import {DeleteOutlined, ExportOutlined, FileOutlined} from '@ant-design/icons';
import useAuthStore from '../../../stores/auth.store';

interface FilesCardProps {
  files: File[];
  installationUuid: string;
  onFileAdded: (file: File) => void;
  onFileDeleted: (fileUuid: string) => void;
}

const FilesCard = ({ files, installationUuid, onFileAdded, onFileDeleted }: FilesCardProps) => {
  const { t } = useTranslation('installation-location', {
    keyPrefix: 'installationLocationDetail.installation.summary.files',
  });
  const [filesWithUrl, setFilesWithUrl] = useState<(File & { url: string })[]>([]);
  const [messageApi, contextHolder] = message.useMessage();
  const isSuperAdminOrCompanyAdminOrAdmin = useAuthStore((state) => state.isSuperAdminOrCompanyAdminOrAdmin);

  useEffect(() => {
    const fetchUrls = async () => {
      const results = await Promise.all(
        files.map(async (file) => ({
          ...file,
          url: await filesApi.createDownloadUrl(file.fileUuid),
        })),
      );
      setFilesWithUrl(results);
    };
    fetchUrls();
  }, [files]);

  const deleteFile = async (file: File) => {
    try {
      await filesApi.deleteFile(file.fileUuid);
      setFilesWithUrl((prev) => prev.filter((f) => f.fileUuid !== file.fileUuid));
      onFileDeleted(file.fileUuid);
    } catch (error) {
      messageApi.error(t('common:errors.generic'));
    }
  };

  return (
    <Card className={'FilesCard'}>
      {contextHolder}
      <CardTitle title={t('title')} />
      <div className={'FilesCard__file'}>
        {filesWithUrl.map((file) => (
          <div className={'FilesCard__file__div'} key={`${file.fileUuid}`}>
            <a className={'FilesCard__file__link'} href={file.url} target="_blank" rel="noreferrer">
              <div className={'FilesCard__file__link_icon'}>
                <FileOutlined />
              </div>
              {file.name}
              <ExportOutlined />
            </a>
            {isSuperAdminOrCompanyAdminOrAdmin && (
              <Popconfirm
                title={t('titleDelete')}
                description={t('checkDelete')}
                okText={t('yes')}
                cancelText={t('cancel')}
                onConfirm={() => {
                  deleteFile(file);
                }}
              >
                <Button type={'text'}>
                  <DeleteOutlined className={'FilesCard__file__div_deleteButton__icon'} />
                </Button>
              </Popconfirm>
            )}
          </div>
        ))}
      </div>
      <AddFileModal installationUuid={installationUuid} onSuccess={onFileAdded} />
    </Card>
  );
};

export default FilesCard;
